import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, Target, FileText, HelpCircle } from 'lucide-react';

// One customer_search_logs row: term, what was shown, and the parts returned.
// Used by Search Analytics and the user view in User Management.

const SOURCE_LABELS = {
  part_enquiry: 'Part Enquiry',
  advanced_search: 'Advanced',
  product_page: 'Product Page',
  scanner: 'Scanner',
};

const formatPrice = (value) => (
  value === null || value === undefined ? '—' : `$${Number(value).toFixed(2)}`
);

const formatQty = (value) => (
  value === null || value === undefined ? '—' : Number(value).toLocaleString()
);

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const diffMs = new Date() - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFullDateTime = (timestamp) => (
  new Date(timestamp).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
);

const getSearchTypeIcon = (type) => {
  switch (type) {
    case 'exact': return <Target className="h-5 w-5 text-green-600" aria-hidden="true" />;
    case 'fuzzy': return <Search className="h-5 w-5 text-blue-600" aria-hidden="true" />;
    case 'description': return <FileText className="h-5 w-5 text-orange-500" aria-hidden="true" />;
    default: return <HelpCircle className="h-5 w-5 text-gray-400" aria-hidden="true" />;
  }
};

const SearchLogRow = ({ search, showUser = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const snapshot = Array.isArray(search.results_snapshot) ? search.results_snapshot : [];
  const hasShown = search.price_shown != null || search.qty_available_shown != null;

  return (
    <div className="bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center min-w-0 flex-1">
          <span className="mr-3 flex-shrink-0">{getSearchTypeIcon(search.search_type)}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-gray-900 truncate">{search.search_term}</span>
              {search.part_found && search.part_found !== search.search_term && (
                <span className="text-xs text-gray-500 whitespace-nowrap">→ {search.part_found}</span>
              )}
              {search.search_source && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 whitespace-nowrap">
                  {SOURCE_LABELS[search.search_source] || search.search_source}
                </span>
              )}
            </div>
            {showUser && (
              <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                <span className="font-medium text-gray-600 truncate">{search.user_name || 'Unknown User'}</span>
                <span>•</span>
                <span className="truncate">{search.company_name}</span>
              </div>
            )}
            {hasShown && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-1">
                <span className="text-gray-600">
                  Price: <span className="font-semibold text-gray-900">{formatPrice(search.price_shown)}</span>
                  {search.unit_code && <span className="text-gray-500"> / {search.unit_code}</span>}
                </span>
                {search.list_price_shown != null && (
                  <span className="text-gray-500">List: {formatPrice(search.list_price_shown)}</span>
                )}
                <span className={Number(search.qty_available_shown) > 0 ? 'text-green-700' : 'text-red-600'}>
                  Avail: {formatQty(search.qty_available_shown)}
                </span>
                {search.qty_on_hand_shown != null && (
                  <span className="text-gray-500">On hand: {formatQty(search.qty_on_hand_shown)}</span>
                )}
              </div>
            )}
            {snapshot.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                {isExpanded ? 'Hide' : 'Show'} {snapshot.length} part{snapshot.length !== 1 ? 's' : ''} returned
                {search.results_count > snapshot.length && ` (of ${search.results_count})`}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end ml-3 flex-shrink-0">
          <div className="flex items-center mb-1">
            {search.search_success ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <XCircle className="w-4 h-4 text-red-500" />
            )}
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap" title={formatFullDateTime(search.created_at)}>
            {formatTimestamp(search.created_at)}
          </span>
        </div>
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 overflow-x-auto">
          <table className="min-w-full text-xs bg-white rounded border border-gray-200">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Part</th>
                <th className="px-2 py-1 text-left font-medium">Description</th>
                <th className="px-2 py-1 text-right font-medium">Price</th>
                <th className="px-2 py-1 text-right font-medium">Avail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {snapshot.map((row, i) => (
                <tr key={`${row.part}-${i}`}>
                  <td className="px-2 py-1 font-mono whitespace-nowrap">{row.part || '—'}</td>
                  <td className="px-2 py-1 text-gray-600">{row.description || '—'}</td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">{formatPrice(row.price)}</td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">{formatQty(row.qty_available)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SearchLogRow;
